// SPDX-License-Identifier: Apache-2.0
import Feature from 'ol/Feature';
import { GeoJSON } from 'ol/format';
import GirafeSingleton from '../../base/GirafeSingleton';
import IGirafeContext from '../context/icontext';
import { Extent } from 'ol/extent';
import { createBufferedExtentFromFeature } from '../geometrytools';

// The default provider is a container for search results that do not result from a provider search (e.g. map re-centering)
export const SEARCH_PROVIDER_DEFAULT = 'default';
// The default search group contains search results that do not belong to a specific category
export const SEARCH_GROUP_DEFAULT = 'default';
// The map recenter search group contains the coordinates for re-centering the map
export const SEARCH_GROUP_RECENTER_MAP = 'recenter_map';

abstract class AbstractSearchClient extends GirafeSingleton {
  public readonly type: string;
  public readonly providerName: string;
  protected url: URL;
  protected readonly resultSrid;
  protected readonly searchTermPlaceholder = '###SEARCHTERM###';
  protected readonly searchLangPlaceholder = '###SEARCHLANG###';
  protected readonly geoJsonFormatter = new GeoJSON();

  protected constructor(context: IGirafeContext, type: string, name: string, url: string, resultSrid: string) {
    super(context);
    this.type = type;
    this.providerName = name;
    this.url = new URL(url);
    this.resultSrid = resultSrid;
  }

  public override initializeSingleton() {
    super.initializeSingleton();
  }

  protected get mapProjection() {
    return this.context.mapManager.getMap().getView().getProjection();
  }

  public async requestSearch(term: string, abortController: AbortController): Promise<Feature[]> {
    const url = this.url
      .toString()
      .replace(this.searchTermPlaceholder, term)
      .replace(this.searchLangPlaceholder, this.context.stateManager.state.language as string);
    try {
      const response = await fetch(url, { signal: abortController.signal });
      return await this.parseResponse(response);
    } catch (error) {
      console.error(`Error fetching search results from ${this.url.toString()}:`, error);
      return [];
    }
  }

  /**
   * Parses the response from the server and returns an array of features.
   * Features must have the following properties to be used correctly with the search component:
   * - `provider`
   * - `label`
   * - `label_html`: optional, for styled entries in the search results list
   * - `actions`: optional, a list of SearchResultsActions
   */
  protected async parseResponse(response: Response): Promise<Feature[]> {
    const data = (await response.json()) as GeoJSON;

    const features = this.geoJsonFormatter.readFeatures(data, {
      dataProjection: this.resultSrid,
      featureProjection: this.mapProjection
    });

    return features.map((f) => {
      f.set('provider', this.providerName);
      return f;
    });
  }

  /**
   * Groups results by the default group, which equivalates to no grouping. Should be overridden by the client class
   * to group results by a custom criteria.
   */
  public groupedResults(results: Feature[]): Record<string, Feature[]> {
    return { [SEARCH_GROUP_DEFAULT]: results };
  }

  /**
   * Returns a bbox around the geometry that is 50% larger than the geometry.
   */
  public getZoomToBbox(feature: Feature): Extent | undefined {
    return createBufferedExtentFromFeature(feature);
  }
}

export default AbstractSearchClient;
